import { HistoryMessage } from './types';
import { local, session } from './util';

// Anonymous identity (docs/widget.md §4): localStorage only, never cookies.
// Conversation continuity is per-site (the site-key-suffixed key), stored in
// localStorage so it follows the visitor across pages/tabs and reloads — two
// shops stay separate via their distinct keys, not via tab isolation.

const ANON_KEY = 'chatbi_anon';
const MAX_HISTORY = 40;

/** name + phone the visitor gave the pre-chat gate (config.requireContactInfo) */
export interface Contact {
  name: string;
  phone: string;
}

export class Identity {
  private readonly convKey: string;
  private readonly contactKey: string;

  constructor(siteKey: string) {
    // last 8 chars of the site key are enough to namespace per site
    this.convKey = `chatbi_conv_${siteKey.slice(-8)}`;
    this.contactKey = `chatbi_contact_${siteKey.slice(-8)}`;
  }

  /** the localStorage key backing this conversation — lets the panel match the
   *  cross-tab `storage` event that fires when a sibling tab writes the transcript */
  get conversationStorageKey(): string {
    return this.convKey;
  }

  getAnonId(): string | undefined {
    return local.get(ANON_KEY) ?? undefined;
  }

  setAnonId(anonId: string): void {
    local.set(ANON_KEY, anonId);
  }

  private readConv(): { conversationId?: string; messages: HistoryMessage[] } {
    try {
      const raw = local.get(this.convKey);
      if (raw) {
        const parsed = JSON.parse(raw);
        if (parsed && Array.isArray(parsed.messages)) return parsed;
      }
    } catch {
      /* corrupted state → start fresh */
    }
    return { messages: [] };
  }

  private writeConv(state: { conversationId?: string; messages: HistoryMessage[] }): void {
    local.set(this.convKey, JSON.stringify(state));
  }

  getConversationId(): string | undefined {
    return this.readConv().conversationId;
  }

  setConversationId(id: string): void {
    const state = this.readConv();
    state.conversationId = id;
    this.writeConv(state);
  }

  /** stale/foreign conversation id (server said 404) — drop id, keep transcript */
  clearConversationId(): void {
    const state = this.readConv();
    delete state.conversationId;
    this.writeConv(state);
  }

  // ---- pre-chat contact gate (persisted per site, survives reloads/tabs) ----

  getContact(): Contact | undefined {
    try {
      const raw = local.get(this.contactKey);
      if (!raw) return undefined;
      const parsed = JSON.parse(raw);
      if (parsed && typeof parsed.name === 'string' && typeof parsed.phone === 'string') {
        return parsed;
      }
    } catch {
      /* corrupted → treat as not provided */
    }
    return undefined;
  }

  hasContact(): boolean {
    return this.getContact() !== undefined;
  }

  setContact(contact: Contact): void {
    local.set(this.contactKey, JSON.stringify(contact));
  }

  getHistory(): HistoryMessage[] {
    return this.readConv().messages;
  }

  appendHistory(message: HistoryMessage): void {
    const state = this.readConv();
    state.messages.push(message);
    if (state.messages.length > MAX_HISTORY) state.messages = state.messages.slice(-MAX_HISTORY);
    this.writeConv(state);
  }

  /** retry flow re-sends the last visitor message — drop the stored duplicate first */
  dropLastVisitorMessage(): void {
    const state = this.readConv();
    for (let i = state.messages.length - 1; i >= 0; i--) {
      if (state.messages[i].role === 'v') {
        state.messages.splice(i, 1);
        break;
      }
    }
    this.writeConv(state);
  }

  /** flip a stored form prompt to submitted so a reload re-renders it locked */
  markFormSubmitted(mid: string): void {
    const state = this.readConv();
    const msg = state.messages.find((m) => m.mid === mid && m.form);
    if (msg?.form) {
      msg.form.submitted = true;
      this.writeConv(state);
    }
  }

  /** flip a stored survey prompt to submitted so a reload re-renders it locked */
  markSurveySubmitted(mid: string): void {
    const state = this.readConv();
    const msg = state.messages.find((m) => m.mid === mid && m.survey);
    if (msg?.survey) {
      msg.survey.submitted = true;
      this.writeConv(state);
    }
  }

  // ---- survey suppression (dismiss = per-session per doc §4) ----

  isSurveySuppressed(surveyId: string): boolean {
    return session.get(`chatbi_sq_${surveyId}`) !== null;
  }

  suppressSurvey(surveyId: string): void {
    session.set(`chatbi_sq_${surveyId}`, '1');
  }
}
